/**
 * Willtec Payment Methods Modal Component (Tailwind Edition)
 * Displays installments breakdown (Cartão de Crédito), PIX, and Boleto Bancário.
 * Harmonized with Willtec visual identity.
 * Dynamically recalculates when variant price changes.
 */
(() => {
  if (customElements.get('willtec-payment-methods-modal')) return;

  class WilltecPaymentMethodsModal extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-modal-dialog]');
      this.closeBtns = this.querySelectorAll('[data-modal-close]');
      this.tabBtns = this.querySelectorAll('[data-tab-target]');
      this.panels = this.querySelectorAll('[data-panel]');
      this.installmentsList = this.querySelector('[data-installments-list]');

      this.pixPriceEl = this.querySelector('[data-pix-price-display]');
      this.pixSavingsEl = this.querySelector('[data-pix-savings-display]');
      this.boletoPriceEl = this.querySelector('[data-boleto-price-display]');
      this.boletoSavingsEl = this.querySelector('[data-boleto-savings-display]');

      this.totalInstallments = parseInt(this.dataset.totalInstallments, 10) || 12;
      this.maxFreeInstallments = parseInt(this.dataset.maxFreeInstallments, 10) || parseInt(this.dataset.freeInstallments, 10) || 3;
      this.minInstallmentValue = parseFloat(this.dataset.minInstallment) || 100;
      this.freeInstallments = parseInt(this.dataset.freeInstallments, 10) || 3;
      this.interestRateStr = this.dataset.interestRate || '4.0';
      this.interestRate = parseFloat(this.interestRateStr) || 0;
      this.pixDiscount = parseFloat(this.dataset.pixDiscount) || 0;
      this.boletoDiscount = parseFloat(this.dataset.boletoDiscount) || 0;

      // Close handlers
      this.closeBtns.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.close();
        });
      });

      // Tab handlers
      this.tabBtns.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const target = btn.dataset.tabTarget;
          if (target) this.switchTab(target);
        });
      });

      // Escape key to close
      this.onKeyDown = (e) => {
        if (e.key === 'Escape' && !this.hasAttribute('hidden')) {
          this.close();
        }
      };
      window.addEventListener('keydown', this.onKeyDown);

      // Listen for trigger buttons globally
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-pdp-open-payment-modal]');
        if (trigger) {
          e.preventDefault();
          this.open();
        }
      });
    }

    disconnectedCallback() {
      if (this.onKeyDown) {
        window.removeEventListener('keydown', this.onKeyDown);
      }
    }

    open() {
      this.removeAttribute('hidden');
      // Force reflow
      void this.offsetWidth;
      this.classList.remove('opacity-0', 'pointer-events-none');
      this.classList.add('opacity-100', 'pointer-events-auto');

      if (this.dialog) {
        this.dialog.classList.remove('scale-95');
        this.dialog.classList.add('scale-100');
      }

      document.body.style.overflow = 'hidden';
    }

    close() {
      this.classList.remove('opacity-100', 'pointer-events-auto');
      this.classList.add('opacity-0', 'pointer-events-none');

      if (this.dialog) {
        this.dialog.classList.remove('scale-100');
        this.dialog.classList.add('scale-95');
      }

      document.body.style.overflow = '';
      setTimeout(() => {
        if (this.classList.contains('opacity-0')) {
          this.setAttribute('hidden', '');
        }
      }, 200);
    }

    switchTab(tabId) {
      const activeTabClasses = ['bg-white', 'text-black', 'font-bold', 'border-[#DDDDDD]', 'border-b-white', 'is-active'];
      const inactiveTabClasses = ['bg-transparent', 'text-[#71717A]', 'font-semibold', 'border-transparent', 'hover:text-[#FE3B40]'];

      this.tabBtns.forEach((btn) => {
        const isActive = btn.dataset.tabTarget === tabId;
        btn.setAttribute('aria-selected', isActive ? 'true' : 'false');

        if (isActive) {
          btn.classList.remove(...inactiveTabClasses);
          btn.classList.add(...activeTabClasses);
        } else {
          btn.classList.remove(...activeTabClasses);
          btn.classList.add(...inactiveTabClasses);
        }
      });

      this.panels.forEach((panel) => {
        if (panel.dataset.panel === tabId) {
          panel.classList.remove('hidden');
          panel.classList.add('block');
        } else {
          panel.classList.remove('block');
          panel.classList.add('hidden');
        }
      });
    }

    updatePrice(priceCents) {
      const price = parseInt(priceCents, 10);
      if (isNaN(price) || price <= 0) return;

      this.dataset.basePrice = price;

      // Calculate effective free installments based on min installment value
      const minInstCents = (this.minInstallmentValue || 100) * 100;
      const possibleFree = minInstCents > 0 ? Math.floor(price / minInstCents) : this.maxFreeInstallments;
      const effectiveFree = Math.max(1, Math.min(this.maxFreeInstallments, possibleFree));

      // Update installments list
      if (this.installmentsList) {
        let rowsHtml = '';
        for (let i = 1; i <= this.totalInstallments; i++) {
          const isFree = i <= effectiveFree || this.interestRate === 0;
          let rowTotal = price;
          let conditionLabel = 'sem juros';

          if (!isFree) {
            const factor = 1 + (i * (this.interestRate / 100));
            rowTotal = Math.round(price * factor);
            conditionLabel = `com juros de ${this.interestRateStr}% a.m.`;
          }

          const instAmount = Math.round(rowTotal / i);

          // Parcela mínima deve ser sempre respeitada com ou sem juros (1x sempre permitido)
          if (i > 1 && minInstCents > 0 && instAmount < minInstCents) {
            break;
          }

          rowsHtml += `
            <div class="flex items-center px-3.5 sm:px-4 py-2.5 text-[12px] sm:text-[13px] text-black gap-2 sm:gap-4 even:bg-white odd:bg-[#F9F9F9] transition-colors">
              <span class="w-7 sm:w-8 font-bold shrink-0 text-black">${i}x</span>
              <span class="w-24 sm:w-32 font-semibold shrink-0 text-black">de ${this.formatMoney(instAmount)}</span>
              <span class="font-normal text-[#303032] shrink-0 hidden xs:inline sm:inline">Total ${this.formatMoney(rowTotal)}</span>
              <span class="ml-auto text-right text-[#71717A] text-[11px] sm:text-[12px] shrink-0 whitespace-nowrap">${conditionLabel}</span>
            </div>
          `;
        }
        this.installmentsList.innerHTML = rowsHtml;
      }

      // Update PIX
      if (this.pixPriceEl) {
        if (this.pixDiscount > 0) {
          const pixPrice = Math.round(price * (1 - this.pixDiscount / 100));
          const savings = price - pixPrice;
          this.pixPriceEl.textContent = this.formatMoney(pixPrice);
          if (this.pixSavingsEl) {
            this.pixSavingsEl.textContent = this.formatMoney(savings);
          }
        } else {
          this.pixPriceEl.textContent = this.formatMoney(price);
        }
      }

      // Update Boleto
      if (this.boletoPriceEl) {
        if (this.boletoDiscount > 0) {
          const boletoPrice = Math.round(price * (1 - this.boletoDiscount / 100));
          const savings = price - boletoPrice;
          this.boletoPriceEl.textContent = this.formatMoney(boletoPrice);
          if (this.boletoSavingsEl) {
            this.boletoSavingsEl.textContent = this.formatMoney(savings);
          }
        } else {
          this.boletoPriceEl.textContent = this.formatMoney(price);
        }
      }
    }

    formatMoney(cents) {
      return (cents / 100).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      });
    }
  }

  customElements.define('willtec-payment-methods-modal', WilltecPaymentMethodsModal);
})();
